import { useTranslation } from "react-i18next";
import { Panel } from "@nbs/ui";
import type { IndicateurDetailFragment } from "../types/graphql";
import { N2IndicatorSeriesPanel } from "./N2IndicatorSeriesPanel";

export interface N2IndicateurDetailProps {
  readonly indicateur: IndicateurDetailFragment;
}

/**
 * Minimal target render for `/n2?indicateurId=...` once resolved (§4.3):
 * the raw, already-authorized scalar fields the existing `indicateur(id)`
 * query returns — no join with `readingN1.priorities`/`risks`/`trends`,
 * no recomputed aggregate, no edit affordance, no admin action (§4.3/§15).
 *
 * `libelle` (the business label) is the heading — never the raw `id`
 * alone as the sole accessible label (§8 "aucun identifiant technique
 * présenté comme seul libellé accessible si un libellé métier est
 * disponible"); `id` is still shown, but as one labelled `<dt>/<dd>` pair
 * among others, exactly like `ProposalSourcePanel`'s `<dl>` convention
 * (`features/review/components/ProposalSourcePanel.tsx`).
 */
export function N2IndicateurDetail({ indicateur }: N2IndicateurDetailProps) {
  const { t } = useTranslation();

  return (
    <Panel raised aria-labelledby="n2-target-indicateur-heading">
      <h2 id="n2-target-indicateur-heading" className="nbs-heading nbs-heading--2">
        {indicateur.libelle}
      </h2>
      <dl className="nbs-n2-target-page__kv">
        <div>
          <dt>{t("n2.target.columns.id")}</dt>
          <dd>{indicateur.id}</dd>
        </div>
        <div>
          <dt>{t("n2.target.columns.uniteDefaut")}</dt>
          <dd>{indicateur.uniteDefaut}</dd>
        </div>
        <div>
          <dt>{t("n2.target.columns.definition")}</dt>
          <dd>{indicateur.definition}</dd>
        </div>
        <div>
          <dt>{t("n2.target.columns.frequence")}</dt>
          <dd>{indicateur.frequence}</dd>
        </div>
      </dl>
      <N2IndicatorSeriesPanel indicateurId={indicateur.id} />
    </Panel>
  );
}
